(() => {
  'use strict';
  if (window.KidAccountFlow !== 'shopify-email-phone') return;
  const LOGIN_PATH = '/pages/member-login';
  const BROWSER_KEY = 'kid_native_browser_v1';
  const LOGOUT_SIGNAL = 'kid_native_logout_v1';
  const CHECKOUT_API = '/apps/kid-sms-account/native/checkout';
  const keyPattern = /^[a-f0-9]{64}$/;
  let leaving = false;
  let intentVersion = 0;
  let gateController = null;
  const pendingControls = new Set();

  function url(value) {
    try {
      const parsed = new URL(value, location.origin);
      return parsed.origin === location.origin ? parsed : null;
    } catch { return null; }
  }
  function isCheckout(value) { const target = url(value); return !!target && /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?checkouts?(?:\/|$)/.test(target.pathname); }
  function nativeLogin(destination) {
    const returnPath = `${LOGIN_PATH}?` + new URLSearchParams({view: 'kid-phone-verify', kid_native_login: '1', return_to: destination});
    return '/customer_authentication/login?' + new URLSearchParams({return_to: returnPath});
  }
  function isAccount(value) {
    const target = url(value);
    return !!target && (/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?account(?:\/login|\/register)?\/?$/.test(target.pathname) || target.pathname === LOGIN_PATH ||
      target.pathname === '/customer_authentication/login' && target.searchParams.get('return_to')?.startsWith(`${LOGIN_PATH}?view=kid-phone-verify&`));
  }
  const accountLabel = 'เข้าสู่ระบบ / บัญชีของฉัน';
  const originalLabels = new WeakMap();
  function normalizeAccountLabel(node) {
    if (!(node instanceof Element)) return;
    const account = node.matches('[data-kid-sms-login]') || node.matches('a[href]') && isAccount(node.getAttribute('href'));
    if (!account) {
      const original = originalLabels.get(node);
      if (original) {
        for (const attribute of ['aria-label', 'title']) {
          if (node.getAttribute(attribute) !== accountLabel) continue;
          if (original[attribute] === null) node.removeAttribute(attribute);
          else node.setAttribute(attribute, original[attribute]);
        }
        originalLabels.delete(node);
      }
      return;
    }
    if (!originalLabels.has(node)) originalLabels.set(node, {'aria-label': node.getAttribute('aria-label'), title: node.getAttribute('title')});
    for (const attribute of ['aria-label', 'title']) if (node.getAttribute(attribute) !== accountLabel) node.setAttribute(attribute, accountLabel);
    // The link itself points to Shopify, including open-in-new-tab gestures.
    if (node.matches('a[href]') && node.getAttribute('href') !== nativeLogin('/account')) node.setAttribute('href', nativeLogin('/account'));
  }
  function normalizeAccountLinks(root) {
    normalizeAccountLabel(root);
    root.querySelectorAll?.('a[href], [data-kid-sms-login]').forEach(normalizeAccountLabel);
  }
  normalizeAccountLinks(document);
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes') normalizeAccountLabel(record.target);
        else record.addedNodes.forEach(normalizeAccountLinks);
      }
    }).observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'aria-label', 'title', 'data-kid-sms-login']});
  }
  function path(event) { return typeof event.composedPath === 'function' ? event.composedPath() : [event.target]; }
  function normalClick(event) { return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey; }
  function isLogout(value) {
    const target = url(value);
    return !!target && /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:account\/logout|customer_authentication\/logout)\/?$/.test(target.pathname);
  }
  function browserKey() {
    try {
      let key = sessionStorage.getItem(BROWSER_KEY);
      if (!keyPattern.test(key || '')) {
        key = Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
        sessionStorage.setItem(BROWSER_KEY, key);
      }
      return key;
    } catch { return null; }
  }
  async function checkPhone() {
    const key = browserKey();
    if (!key) return false;
    const controller = new AbortController();
    gateController = controller;
    const timer = window.setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(CHECKOUT_API + '/status', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: {'Content-Type': 'application/json'}, body: JSON.stringify({browserKey: key})
      });
      if (!response.ok) return false;
      const result = await response.json();
      // No customer ID or authenticated flag from browser storage can skip this request.
      return result.ready === true && sessionStorage.getItem(BROWSER_KEY) === key;
    } catch { return false; }
    finally {
      window.clearTimeout(timer);
      if (gateController === controller) gateController = null;
    }
  }
  function clearBrowserCheck(broadcast = false) {
    intentVersion++;
    gateController?.abort();
    reset();
    try {
      const key = sessionStorage.getItem(BROWSER_KEY);
      sessionStorage.removeItem(BROWSER_KEY);
      if (keyPattern.test(key || '')) {
        // Best effort; Shopify logout proceeds immediately. Future status calls
        // still require Shopify's current signed identity even if this request fails.
        void fetch(CHECKOUT_API + '/logout', {method: 'POST', credentials: 'same-origin', cache: 'no-store', keepalive: true,
          headers: {'Content-Type': 'application/json'}, body: JSON.stringify({browserKey: key})}).catch(() => {});
      }
    } catch { /* Blocked storage falls back to the original native-login flow. */ }
    if (broadcast) {
      try { localStorage.setItem(LOGOUT_SIGNAL, `${Date.now()}:${Math.random()}`); } catch { /* Optional cross-tab notification. */ }
    }
  }
  function enter(destination, control) {
    if (leaving) return;
    leaving = true;
    control?.setAttribute?.('aria-busy', 'true');
    if (control) pendingControls.add(control);
    const version = ++intentVersion;
    const gate = checkPhone();
    const cart = destination === '/checkout' ? window.Alpine?.store?.('xCartHelper') : null;
    let cartReady = Promise.resolve();
    let timer;
    if (cart && typeof cart.waitForCartUpdate === 'function') {
      // Eurus queues quantity, delivery-field and note updates before its native
      // form submits. Do not leave the page while those writes are still running.
      cartReady = Promise.race([
        Promise.resolve().then(() => cart.waitForCartUpdate()),
        new Promise((_, reject) => { timer = window.setTimeout(() => reject(new Error('cart_timeout')), 15000); }),
      ]);
    }
    void Promise.all([gate, cartReady]).then(([ready]) => {
      if (version !== intentVersion) return;
      if (cart?.validated === false) { reset(); return; }
      // An expired/unknown identity or network failure keeps the original flow.
      location.assign(ready ? destination : nativeLogin(destination));
    }).catch(() => {
        if (version !== intentVersion) return;
        reset();
        let notice = document.querySelector('[data-kid-native-checkout-notice]');
        if (!notice) {
          notice = document.createElement('div');
          notice.setAttribute('data-kid-native-checkout-notice', '');
          notice.setAttribute('role', 'alert');
          notice.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:2147483647;max-width:90vw;padding:12px 18px;background:#fff0e8;color:#983f29;border:1px solid #edc8b7;border-radius:12px;font:15px Anuphan,system-ui,sans-serif';
          document.body.appendChild(notice);
        }
        notice.textContent = 'ยังบันทึกตะกร้าไม่สำเร็จ กรุณาลองกด Checkout อีกครั้ง';
    }).finally(() => window.clearTimeout(timer));
  }

  function reset() {
    leaving = false;
    pendingControls.forEach(node => node.removeAttribute('aria-busy'));
    pendingControls.clear();
  }

  // Clear our hint without preventing or replacing Shopify's actual logout.
  document.addEventListener('click', event => {
    if (!normalClick(event)) return;
    const control = path(event).find(node => node instanceof Element && node.matches('a[href]') && isLogout(node.getAttribute('href')));
    if (control) clearBrowserCheck(true);
  }, true);
  document.addEventListener('submit', event => {
    if (event.target instanceof HTMLFormElement && isLogout(event.target.getAttribute('action'))) clearBrowserCheck(true);
  }, true);
  window.addEventListener('storage', event => { if (event.key === LOGOUT_SIGNAL) clearBrowserCheck(false); });

  // Account links are handled before obsolete popup hooks. Logout links are not matched.
  document.addEventListener('click', event => {
    if (!normalClick(event)) return;
    const control = path(event).find(node => node instanceof Element &&
      (node.matches('[data-kid-sms-login]') || node.matches('a[href]') && isAccount(node.getAttribute('href'))));
    if (!control || control.getAttribute('target') === '_blank') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    enter('/account', control);
  }, true);

  // Use the bubbling phase so the theme can reject checkout (terms/cart rules)
  // first. Submit buttons continue to the submit event for native form validation.
  document.addEventListener('click', event => {
    if (!normalClick(event)) return;
    const control = path(event).find(node => node instanceof Element &&
      (node.matches('a[href]') && isCheckout(node.getAttribute('href')) || node.matches('button[name="checkout"], input[name="checkout"]')));
    if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true' || control.getAttribute('target') === '_blank') return;
    if (control.form && control.type !== 'button') return;
    event.preventDefault();
    enter('/checkout', control);
  });

  document.addEventListener('submit', event => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const control = event.submitter;
    if (!(control?.getAttribute('name') === 'checkout' || isCheckout(form.getAttribute('action')))) return;
    if (control?.disabled || control?.getAttribute('aria-disabled') === 'true') return;
    // Browser form constraints have passed before submit fires; no raw form.submit().
    event.preventDefault();
    enter('/checkout', control || form);
  });

  window.addEventListener('pageshow', () => {
    intentVersion++;
    gateController?.abort();
    reset();
  });
  window.addEventListener('pagehide', () => { intentVersion++; gateController?.abort(); reset(); });
})();
